'use client'

import { CartItem } from './types'
import { useCart } from '@payloadcms/plugin-ecommerce/client/react'
import clsx from 'clsx'
import React, { useEffect, useState, KeyboardEvent } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

interface Props {
    item: CartItem
}

export function CartItemQuantityInput({ item }: Props) {
    const { decrementItem, incrementItem, addItem, removeItem, isLoading } = useCart()
    const [value, setValue] = useState<string>(String(item.quantity || 1))
    const [isUpdating, setIsUpdating] = useState(false)

    // Sync local state when item quantity changes from external updates
    useEffect(() => {
        if (!isUpdating) {
            setValue(String(item.quantity || 1))
        }
    }, [item.quantity, isUpdating])

    const handleUpdate = async () => {
        const newQuantity = parseInt(value, 10)
        const currentQuantity = item.quantity || 1

        if (isNaN(newQuantity) || newQuantity < 0) {
            // Revert to current quantity if invalid
            setValue(String(currentQuantity))
            return
        }

        if (newQuantity === currentQuantity) {
            return
        }

        setIsUpdating(true)

        try {
            if (newQuantity === 0) {
                if (item.id) {
                    await removeItem(item.id as any) // Cast ID if needed based on version
                }
            } else if (newQuantity > currentQuantity) {
                // Increasing quantity
                const diff = newQuantity - currentQuantity

                // Extract product and variant IDs
                const productId = typeof item.product === 'object' ? item.product?.id : item.product
                const variantId = typeof item.variant === 'object' ? item.variant?.id : item.variant

                if (!productId) {
                    console.error('Cannot update quantity: missing product ID')
                    return
                }

                // Check inventory if available
                const target =
                    (typeof item.variant === 'object' ? item.variant : null) ||
                    (typeof item.product === 'object' ? item.product : null)

                if (target && typeof target === 'object' && 'inventory' in target) {
                    const inventory = (target as any).inventory
                    if (typeof inventory === 'number' && newQuantity > inventory) {
                        toast.error(`Only ${inventory} items available in stock`)
                        setValue(String(currentQuantity))
                        setIsUpdating(false)
                        return
                    }
                }

                await addItem(
                    {
                        product: productId,
                        variant: variantId,
                    },
                    diff,
                )
            } else {
                // Decreasing quantity
                // Since we don't have a direct "set quantity" method and removeItem -> addItem 
                // risks losing the item position or failing on add, we loop decrementItem.
                // For very large reductions, this might be slow, but it's safer for data integrity.
                const diff = currentQuantity - newQuantity

                // Execute decrements in parallel for speed, assuming backend handles concurrency
                if (item.id) {
                    const promises = []
                    for (let i = 0; i < diff; i++) {
                        promises.push(decrementItem(item.id as any))
                    }
                    await Promise.all(promises)
                }
            }
        } catch (error) {
            console.error('Failed to update quantity:', error)
            toast.error('Failed to update quantity')
            setValue(String(currentQuantity))
        } finally {
            setIsUpdating(false)
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur() // Triggers onBlur which calls handleUpdate
        }
    }

    const handleBlur = () => {
        handleUpdate()
    }

    return (
        <div className="relative">
            <input
                type="number"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isLoading || isUpdating}
                className={clsx(
                    "w-12 text-center font-mono text-xs bg-transparent border-none p-0 focus:ring-0 appearance-none h-8 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]",
                    isLoading || isUpdating ? "opacity-50" : ""
                )}
            />
            {(isLoading || isUpdating) && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Loader2 className="w-3 h-3 animate-spin text-[var(--neon-orange)]" />
                </div>
            )}
        </div>
    )
}
